/**
 * azure-functions/shared/emailIdentity.cjs
 *
 * Canonical implementation. Called directly by Azure Functions (via
 * require) and re-exported for the frontend by
 * src/utils/emailIdentity.js — there is exactly one copy of this
 * logic; do not fork it again.
 *
 * Identity matching across tenant-owned email domains.
 *
 * When a tenant lists multiple owned domains (e.g. after a corporate rebrand
 * from uruzen.com to uruz.eu), addresses sharing a local part across any of
 * those domains resolve to the same identity. Addresses on non-listed domains
 * keep their full form and only match exactly.
 *
 * All functions are pure and defensive on bad input.
 */

function normaliseDomains(tenantDomains) {
  const out = new Set();
  if (!Array.isArray(tenantDomains)) return out;
  for (const d of tenantDomains) {
    if (typeof d !== 'string') continue;
    const v = d.trim().toLowerCase();
    if (v) out.add(v);
  }
  return out;
}

/**
 * Returns a canonical form of `email` for identity matching, or null if the
 * input is not a usable email.
 *
 * - Trimmed and lowercased.
 * - If the domain is in `tenantDomains`, the domain is collapsed to a sentinel
 *   so that any two listed-domain addresses with the same local part canonicalise
 *   to the same string.
 * - Otherwise the full `local@domain` form is returned.
 */
function canonicaliseEmail(email, tenantDomains) {
  if (!email || typeof email !== 'string') return null;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return null;
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  const domains = normaliseDomains(tenantDomains);
  if (domains.has(domain)) return `${local}@__tenant__`;
  return `${local}@${domain}`;
}

/** True iff `a` and `b` are the same identity given `tenantDomains`. */
function emailsMatch(a, b, tenantDomains) {
  const ca = canonicaliseEmail(a, tenantDomains);
  const cb = canonicaliseEmail(b, tenantDomains);
  if (!ca || !cb) return false;
  return ca === cb;
}

/** Finds the first employee whose `.email` matches `email` per `emailsMatch`. */
function findEmployeeByEmail(employees, email, tenantDomains) {
  const target = canonicaliseEmail(email, tenantDomains);
  if (!target) return null;
  for (const emp of employees || []) {
    if (canonicaliseEmail(emp && emp.email, tenantDomains) === target) return emp;
  }
  return null;
}

/**
 * Builds an O(1) lookup over `employees` keyed by canonical email.
 *
 * Returns an object with `get(email)`, `set(email, value)`, `has(email)` that
 * all canonicalise the email before reading/writing the underlying Map. The
 * stored values are whatever the caller passes in (typically an index or the
 * employee reference itself).
 */
function buildEmployeeEmailIndex(employees, tenantDomains, valueFn) {
  const map = new Map();
  const project = typeof valueFn === 'function' ? valueFn : (emp, idx) => idx;
  (employees || []).forEach((emp, idx) => {
    const key = canonicaliseEmail(emp && emp.email, tenantDomains);
    if (key && !map.has(key)) map.set(key, project(emp, idx));
  });
  return {
    get(email) {
      const k = canonicaliseEmail(email, tenantDomains);
      return k ? map.get(k) : undefined;
    },
    set(email, value) {
      const k = canonicaliseEmail(email, tenantDomains);
      if (k) map.set(k, value);
    },
    has(email) {
      const k = canonicaliseEmail(email, tenantDomains);
      return k ? map.has(k) : false;
    },
  };
}

module.exports = {
  canonicaliseEmail,
  emailsMatch,
  findEmployeeByEmail,
  buildEmployeeEmailIndex,
};
